import dayjs from 'dayjs'
import { Divider, Popover } from 'antd'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import { Text } from '@lmc/ui-kit/src/general'
import { NAMESPACE } from 'locale'
import { MEDICATION_ACTIONS } from '../utils'
import AdministeredContent from './contents/AdministeredContent'
import RefusedContent from './contents/RefusedContent'
import PausedContent from './contents/PausedContent'
import NotNeededContent from './contents/NotNeededContent'

type OutcomePopoverProps = {
  children: React.ReactNode
  outcome: {
    action: string
    recordDetails?: any
  }
  record: any
  type: 'scheduled' | 'prn'
  residentFirstName: string
}

const OutcomePopover = ({
  children,
  outcome,
  record,
  type,
  residentFirstName
}: OutcomePopoverProps) => {
  const { t } = useTranslation(NAMESPACE.EMAR_V2)

  const action = MEDICATION_ACTIONS.find(
    (a) => a.translationKey === outcome.action
  )

  if (!action) {
    return <>{children}</>
  }

  if (
    ![
      'ADMINISTERED',
      'REFUSED',
      'PAUSED',
      'DISCONTINUED',
      'NOT_SCHEDULED_OR_NOT_NEEDED'
    ].includes(outcome.action)
  ) {
    return <>{children}</>
  }

  return (
    <Popover
      placement="bottomLeft"
      trigger="hover"
      overlayInnerStyle={{
        borderRadius: '9px',
        padding: '12px 16px',
        width: '368px'
      }}
      title={
        <>
          <PopoverTitle>
            {action.icon && <TitleIcon>{action.icon}</TitleIcon>}
            {action.code && <TitleCode>{action.code}</TitleCode>}
            <TitleText>{t(action.translationKey)}</TitleText>
          </PopoverTitle>
          {outcome.action !== 'DISCONTINUED' && <StyledDivider />}
        </>
      }
      content={
        outcome.action === 'DISCONTINUED' ? null : (
          <PopoverContent>
            {outcome.action === 'ADMINISTERED' && (
              <AdministeredContent
                record={record}
                recordDetails={outcome.recordDetails}
                type={type}
                residentFirstName={residentFirstName}
              />
            )}
            {outcome.action === 'REFUSED' && (
              <RefusedContent
                record={record}
                recordDetails={outcome.recordDetails}
              />
            )}
            {outcome.action === 'PAUSED' && (
              <PausedContent
                record={record}
                recordDetails={outcome.recordDetails}
              />
            )}
            {outcome.action === 'NOT_SCHEDULED_OR_NOT_NEEDED' && (
              <NotNeededContent
                record={record}
                recordDetails={outcome.recordDetails}
              />
            )}
            {outcome.recordDetails && (
              <>
                <StyledDivider />
                <InfoRowHorizontal>
                  <InfoItem>
                    <Label level={2}> {t('TIME_RECORDED')}</Label>
                    <Value>
                      {outcome.recordDetails.timeRecorded
                        ? dayjs(outcome.recordDetails.timeRecorded).format(
                            'DD-MM-YYYY HH:mm'
                          )
                        : ''}
                    </Value>
                  </InfoItem>
                  <InfoItem>
                    <Label level={2}>{t('RECORDED_BY')}</Label>
                    <Value>{outcome.recordDetails.recordedBy || ''}</Value>
                  </InfoItem>
                </InfoRowHorizontal>
                {outcome.recordDetails.witnessedBy && (
                  <InfoRow>
                    <Label level={2}>{t('WITNESSED_BY')}</Label>
                    <Value>{outcome.recordDetails.witnessedBy}</Value>
                  </InfoRow>
                )}
              </>
            )}
          </PopoverContent>
        )
      }
    >
      <PopoverTrigger>{children}</PopoverTrigger>
    </Popover>
  )
}

const PopoverTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const TitleIcon = styled.div`
  display: flex;
  align-items: center;
  svg {
    width: 16px;
    height: 16px;
  }
`

const TitleCode = styled(Text)`
  font-weight: 600;
  color: ${({ theme }) => theme.color.neutral.color3};
`

const TitleText = styled(Text)`
  font-weight: 700;
  color: ${({ theme }) => theme.color.neutral.color3};
`

const PopoverContent = styled.div`
  display: flex;
  flex-direction: column;
`

const InfoRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const InfoRowHorizontal = styled.div`
  display: flex;
  flex-direction: row;
`

const InfoItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
`

const Label = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color5};
  font-weight: 500;
`

const Value = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color3};
  font-weight: 400;
`

const PopoverTrigger = styled.div`
  cursor: pointer;
  width: 100%;
  height: 100%;
`

const StyledDivider = styled(Divider)`
  margin: 10px 0;
`

export default OutcomePopover
